import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Play, CheckCircle, Dumbbell } from 'lucide-react';

const ExercisePlan = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadPlan = async () => {
      try {
        const api = createAuthAPI();
        const response = await api.get('/client/exercise-plan', {
          signal: abortController.signal
        });
        if (isMounted) {
          setPlan(response.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          toast.error('Error al cargar plan de ejercicios');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const loadPlan = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/client/exercise-plan');
      setPlan(response.data);
    } catch (error) {
      toast.error('Error al cargar plan de ejercicios');
    } finally {
      setLoading(false);
    }
  };

  const markVideoComplete = async (videoId) => {
    try {
      const api = createAuthAPI();
      await api.post(`/client/video-view?video_id=${videoId}`);
      toast.success('¡Excelente trabajo! Video completado.');
      // Show motivational message
      setTimeout(() => {
        toast.success('¡Sigue así! Cada esfuerzo cuenta en tu camino hacia el bienestar.');
      }, 1000);
    } catch (error) {
      toast.error('Error al registrar visualización');
    }
  };

  if (loading) {
    return <div>Cargando plan de ejercicios...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="pharmacy-card bg-gradient-to-r from-green-50 to-emerald-50" data-testid="exercise-plan-info">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-600 p-4 rounded-full">
              <Dumbbell className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-green-800">{plan?.fase_ejercicio}</h3>
              <p className="text-gray-600">Llevas {plan?.dias_transcurridos} días en el programa</p>
              <p className="text-lg font-semibold text-green-700 mt-2">
                Objetivo de hoy: {plan?.duracion_objetivo} minutos de ejercicio
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        {plan?.videos && plan.videos.length > 0 ? (
          plan.videos.map((video) => (
            <Card key={video.id} className="pharmacy-card" data-testid={`exercise-video-${video.id}`}>
              <CardHeader>
                <CardTitle className="text-green-800">{video.titulo}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">Duración: {video.duracion_minutos} minutos</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => window.open(video.url, '_blank')}
                    className="pharmacy-button text-white"
                    data-testid={`play-video-${video.id}`}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Ver Video
                  </Button>
                  <Button
                    onClick={() => markVideoComplete(video.id)}
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50"
                    data-testid={`complete-video-${video.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Marcar como Completado
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="pharmacy-card">
            <CardContent className="py-8 text-center">
              <p className="text-gray-600">No hay videos disponibles aún.</p>
              <p className="text-sm text-gray-500 mt-2">
                Mientras tanto, puedes caminar 30 minutos al día.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="pharmacy-card bg-gradient-to-br from-blue-50 to-green-50 border-2 border-blue-300">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-blue-900 mb-4 text-lg flex items-center gap-2">
            💡 Consejos de Ejercicio
          </h4>
          
          {/* Progresión de Tiempo */}
          <div className="bg-white p-4 rounded-lg border border-blue-200 mb-4">
            <h5 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              ⏱️ Progresión Recomendada
            </h5>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 min-w-[140px]">Días 1-5:</span>
                <span>Mínimo <strong>5 minutos</strong> al día</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 min-w-[140px]">Días 6-10:</span>
                <span>Aumenta a <strong>7 minutos</strong> al día</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 min-w-[140px]">Días 11-20:</span>
                <span>Sube a <strong>10 minutos</strong> al día</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-blue-600 min-w-[140px]">Días 21-30:</span>
                <span>Alcanza <strong>12 minutos</strong> al día</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-green-700 min-w-[140px]">Día 31+:</span>
                <span><strong>15 minutos</strong> siempre, ¡y si quieres más... pues más! 👏👏👏💪💪💪</span>
              </li>
            </ul>
          </div>

          {/* Hidratación */}
          <div className="bg-blue-100 p-4 rounded-lg border border-blue-300 mb-4">
            <h5 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              💧 Hidratación
            </h5>
            <p className="text-sm text-blue-800 font-semibold">
              Hidrátate correctamente, <span className="text-blue-900">"siempre con Sales minerales, tu escudo contra la deshidratación."</span>
            </p>
          </div>

          {/* Consejos Adicionales */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300">
            <h5 className="font-bold text-yellow-900 mb-3 flex items-center gap-2">
              ⭐ Consejos Importantes
            </h5>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Realiza los ejercicios de forma <strong>progresiva</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Mantén una buena <strong>hidratación</strong> durante el ejercicio</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>Si tienes alguna <strong>molestia</strong>, consulta con tu farmacéutico</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-600">•</span>
                <span>La <strong>constancia</strong> es clave para obtener resultados</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExercisePlan;